import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Select,
  HStack,
  Button,
  useToast
} from '@chakra-ui/react';
import { cortiLogger } from '../utils/cortiLogger';
import { getSupabase } from '../components/supabaseClient';
import { normalizeRole } from '../config/roles';

const TranscriptionLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'live', 'recorded'
  const [limit, setLimit] = useState(50);
  const toast = useToast();

  useEffect(() => {
    loadLogs();
  }, [filter, limit]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let logsData;
      
      // Check if user is admin to get all logs or just their own
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', session?.user?.id)
        .single();

      if (normalizeRole(userData?.role) === 'admin') {
        logsData = await cortiLogger.getAllTranscriptionLogs(limit);
      } else {
        logsData = await cortiLogger.getUserTranscriptionLogs(limit);
      }

      // Apply filter
      if (filter !== 'all') {
        logsData = logsData.filter(log => log.transcription_type === filter);
      }

      setLogs(logsData);
    } catch (err) {
      console.error('Error loading transcription logs:', err);
      setError('Failed to load transcription logs');
      toast({
        title: 'Error',
        description: 'Failed to load transcription logs',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'partial':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'live':
        return 'blue';
      case 'recorded':
        return 'purple';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" />
        <Text mt={4}>Loading transcription logs...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Transcription Logs</Heading>
      
      {/* Filters */}
      <HStack spacing={4} mb={4}>
        <Select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          width="200px"
        >
          <option value="all">All Types</option>
          <option value="live">Live Only</option>
          <option value="recorded">Recorded Only</option>
        </Select>
        
        <Select 
          value={limit} 
          onChange={(e) => setLimit(Number(e.target.value))}
          width="150px"
        >
          <option value={25}>25 records</option>
          <option value={50}>50 records</option>
          <option value={100}>100 records</option>
        </Select>
        
        <Button onClick={loadLogs} size="sm">
          Refresh
        </Button>
      </HStack>

      {/* Logs Table */}
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Date/Time</Th>
              <Th>User</Th>
              <Th>Patient</Th>
              <Th>Type</Th>
              <Th>Audio File</Th>
              <Th>Duration</Th>
              <Th>Credits Used</Th>
              <Th>Status</Th>
              <Th>Processing Time</Th>
              <Th>Facts</Th>
              <Th>SOAP Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logs.map((log) => (
              <Tr key={log.id}>
                <Td fontSize="xs">{formatDate(log.created_at)}</Td>
                <Td fontSize="xs">{log.user_email}</Td>
                <Td fontSize="xs">{log.patient_name || 'N/A'}</Td>
                <Td>
                  <Badge colorScheme={getTypeColor(log.transcription_type)}>
                    {log.transcription_type}
                  </Badge>
                </Td>
                <Td fontSize="xs" maxW="150px" overflow="hidden" textOverflow="ellipsis">
                  {log.audio_file_name || 'N/A'}
                </Td>
                <Td fontSize="xs">{formatDuration(log.audio_duration_seconds)}</Td>
                <Td fontSize="xs">{log.credit_usage?.toFixed(4) || 'N/A'}</Td>
                <Td>
                  <Badge colorScheme={getStatusColor(log.status)}>
                    {log.status}
                  </Badge>
                </Td>
                <Td fontSize="xs">{formatDuration(log.processing_time_seconds)}</Td>
                <Td fontSize="xs">{log.facts_extracted || 0}</Td>
                <Td>
                  <Badge colorScheme={log.soap_notes_generated ? 'green' : 'gray'}>
                    {log.soap_notes_generated ? 'Yes' : 'No'}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {logs.length === 0 && (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">No transcription logs found</Text>
        </Box>
      )}

      <Text fontSize="sm" color="gray.600" mt={4}>
        Showing {logs.length} transcription logs
      </Text>
    </Box>
  );
};

export default TranscriptionLogs;
